/**
 * byo-v4-reviews.js — Build Your Own v4, page chrome behaviour.
 *
 * Three things, all of them page chrome (BUILD-PLAN.md P1, DESIGN-SPEC.md §2):
 *
 *   1. The reviews pop-out (ReviewsModal, §2.4) — a bottom sheet on the phone,
 *      a right-hand drawer on desktop. Opened from the hero rating badge and
 *      from the proof band's Judge.me button; closed by the scrim, Escape and
 *      the close button.
 *   2. "Start building" (§2.6) — scrolls the builder section fully into view,
 *      centred when the viewport is taller than the section.
 *   3. The phone header slide-away (§2.1) — the theme's own sticky header
 *      slides out of the way once the builder reaches the top, and returns on
 *      scroll up. It also exposes reveal(), which the packing scene calls in a
 *      later phase so the "+1" has a bag to land on.
 *   4. The builder snaps into place (D70) — the first touch inside it brings
 *      it fully on screen, and it settles back there while it is in use.
 *
 * The pop-out's markup is rendered by sections/byo-v4-reviews.liquid, not built
 * here: the review copy belongs to theme-editor blocks, and server-rendered
 * cards cost no layout shift. This file only opens, closes and focuses it.
 *
 * The theme's header section is never edited. This module finds it at runtime
 * and adds classes that only exist in byo-v4.css, which only this template
 * loads — remove the template and the header is exactly as it was
 * (THEME-INTEGRATION.md §3).
 *
 * ES2020 module, no transpilation, no bundler. Loaded as
 * <script type="module"> from the hero and reviews sections; a module with the
 * same URL evaluates once however many sections ask for it.
 */

const SEL = {
  popout: '[data-byo-v4-popout]',
  popoutSheet: '[data-byo-v4-popout-sheet]',
  popoutClose: '[data-byo-v4-popout-close]',
  open: '[data-byo-v4-reviews]',
  start: '[data-byo-v4-start]',
  builder: '[data-byo-v4-builder]',
  stickyHeader: '[data-sticky-header]',
  headerGroup: '.shopify-section-group-header-group',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* §2.1's slide-away is a phone behaviour. 990px is this build's one breakpoint;
   byo-v4.css uses the same number. */
const PHONE = window.matchMedia('(max-width: 989.98px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/* -------------------------------------------------------------------------
   The reviews pop-out
   ------------------------------------------------------------------------- */

let opener = null;

const popout = () => document.querySelector(SEL.popout);

function openReviews(from) {
  const el = popout();
  if (!el || !el.hidden) return;

  opener = from || null;
  el.hidden = false;
  document.documentElement.classList.add('byo-v4-scroll-locked');

  const close = el.querySelector(SEL.popoutClose);
  if (close) close.focus();
}

function closeReviews() {
  const el = popout();
  if (!el || el.hidden) return;

  el.hidden = true;
  document.documentElement.classList.remove('byo-v4-scroll-locked');

  /* Return focus to whatever opened it. The page's scroll position is
     untouched throughout — nothing here scrolls the document. */
  if (opener && document.contains(opener)) opener.focus();
  opener = null;
}

/* Tab stays inside the dialog while it is open. */
function trapFocus(event) {
  const el = popout();
  if (!el || el.hidden || event.key !== 'Tab') return;

  /* getClientRects() rather than offsetParent: the sheet's ancestor is
     position: fixed, where offsetParent is unreliable. */
  const items = [...el.querySelectorAll(FOCUSABLE)].filter(
    (node) => node.getClientRects().length > 0
  );
  if (items.length === 0) return;

  const first = items[0];
  const last = items[items.length - 1];

  /* Focus can sit outside the dialog even while it is open: clicking any
     non-focusable content in the sheet — a review's text, the score, a star
     bar — moves activeElement to <body>. That matches neither first nor last,
     so without this the default Tab walks into the scroll-locked page behind
     the scrim, which the scrim hides from view. Pull it back in. */
  if (!el.contains(document.activeElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/* -------------------------------------------------------------------------
   "Start building" — DESIGN-SPEC.md §2.6

     offset = max(0, (viewportHeight - sectionHeight) / 2)
     scrollTo(sectionTop - offset, smooth)
   ------------------------------------------------------------------------- */

/** Where the page sits with the builder fully on screen. On a phone the frame
    is a full screen tall (D129), so it docks at the sticky header's foot. */
function builderTop(el) {
  const rect = el.getBoundingClientRect();
  const offset = Math.max(0, (window.innerHeight - rect.height) / 2);
  return Math.round(rect.top + window.scrollY - Math.max(offset, browseReach(el)));
}

/** How much further browse mode takes the page on a phone: the header's
    height (D129, byo-v4-fill.js). Zero elsewhere. */
function browseReach(el) {
  return PHONE.matches ? parseFloat(el.style.getPropertyValue('--byo-v4-header-h')) || 0 : 0;
}

function startBuilding() {
  const el = document.querySelector(SEL.builder);
  if (!el) return;

  window.scrollTo({
    top: builderTop(el),
    behavior: REDUCED.matches ? 'auto' : 'smooth',
  });
}

/* -------------------------------------------------------------------------
   The builder snaps into place — D70

   Alan, 26 Sep 2026: someone who scrolls past "Start building" and begins
   using the builder with only part of it on screen should be brought to it
   and kept there, "so that there's never a situation in which they're
   progressing through the BYO journey, but only half of it is displayed.
   This should be common across all devices."

   The first press, tap or keyboard focus inside the builder scrolls it to
   where "Start building" puts it. From then on it is engaged: whenever a
   scroll settles with most of it still on screen, it settles back into
   place. A scroll that takes it mostly off screen releases it, so the page
   above and below it stays reachable. The journey scrolls inside the
   builder's own frame (overscroll-behavior: contain), so browsing products
   never moves the page in the first place.
   ------------------------------------------------------------------------- */

let engaged = false;
let snappedAt = 0;
let touching = false;
let settleTimer = 0;

/* A few pixels off is on screen: the phone's own toolbars nudge the numbers,
   and chasing them would start a scroll that never ends. */
const SNAP_SLOP = 8;

function snapBuilder() {
  const el = document.querySelector(SEL.builder);
  if (!el) return;
  let top = builderTop(el);
  /* Docked and browsing (D129) are both in place. Between them, the nearer. */
  const reach = browseReach(el);
  const y = window.scrollY;
  if (Math.abs(y - top) <= SNAP_SLOP || (reach && Math.abs(y - top - reach) <= SNAP_SLOP)) return;
  if (reach && y > top + reach / 2 && y < top + reach) top += reach;
  snappedAt = Date.now();
  window.scrollTo({ top, behavior: REDUCED.matches ? 'auto' : 'smooth' });
}

function engage(event) {
  const el = document.querySelector(SEL.builder);
  if (!el || !event.target || !el.contains(event.target)) return;
  engaged = true;
  snapBuilder();
}

function settle() {
  settleTimer = 0;
  if (!engaged || touching) return;
  /* Our own smooth scroll is still landing. */
  if (Date.now() - snappedAt < 900) return;
  const el = document.querySelector(SEL.builder);
  if (!el) return;
  const r = el.getBoundingClientRect();
  const seen = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
  if (seen >= 0.6 * Math.min(r.height, window.innerHeight)) snapBuilder();
  else engaged = false;
}

function settleSoon() {
  if (!engaged) return;
  clearTimeout(settleTimer);
  settleTimer = setTimeout(settle, 250);
}

/* -------------------------------------------------------------------------
   The phone header slide-away — DESIGN-SPEC.md §2.1
   ------------------------------------------------------------------------- */

function themeHeader() {
  const sticky = document.querySelector(SEL.stickyHeader);
  /* The sticky rule is on the section wrapper, not on the element carrying the
     attribute, so move up to the wrapper before transforming anything. */
  if (sticky) return sticky.closest('[id^="shopify-section-"]') || sticky;

  /* Fallback for a theme whose header is not set to sticky. The header group's
     FIRST section is the announcement bar, not the header, so match the element
     that actually carries the banner role. Note this theme's header is a
     <data-island class="site-header" role="banner"> (sections/header.liquid) —
     the only literal <header> in the theme is in gift-card.liquid, so matching
     on the tag alone never fires and the slide-away silently did nothing
     whenever "sticky header" was switched off. */
  const header = document.querySelector(
    `${SEL.headerGroup} [role="banner"], ${SEL.headerGroup} header`
  );
  if (header) return header.closest('[id^="shopify-section-"]') || header;
  return null;
}

let pinned = false;
let ticking = false;

function syncHeader() {
  ticking = false;

  const header = themeHeader();
  const builder = document.querySelector(SEL.builder);
  if (!header || !builder) return;

  /* The design scopes the slide-away to the phone, so the transition is only
     attached there — the theme's header is shared chrome and should carry
     nothing from this page at desktop widths. */
  if (!PHONE.matches) {
    header.classList.remove('byo-v4-header-hidden', 'byo-v4-header-slide');
    return;
  }

  /* Pinned holds the header in view for the packing scene. Keep the transition
     class while doing it: dropping both in the same frame removes the very rule
     the 0.3s slide lives on (byo-v4.css), so the header would snap back rather
     than slide as §2.1 specifies. */
  if (pinned) {
    header.classList.add('byo-v4-header-slide');
    header.classList.remove('byo-v4-header-hidden');
    return;
  }

  header.classList.add('byo-v4-header-slide');

  /* The design measures the builder's top against 60% of the header's height:
     hide once the section has come that far up the screen, show again as soon
     as it drops back below it — which is what brings the header back on the
     way up. */
  const top = builder.getBoundingClientRect().top;
  header.classList.toggle('byo-v4-header-hidden', top <= header.offsetHeight * 0.6);
}

function onScroll() {
  if (ticking) return;
  ticking = true;
  window.requestAnimationFrame(syncHeader);
}

/**
 * Hold the header in view, whatever the scroll position — the packing scene
 * calls this so the bag badge's "+1" has somewhere to land (§2.1), and calls
 * it again with false when the scene is over.
 *
 * @param {boolean} [on=true]
 */
export function reveal(on = true) {
  pinned = on;
  syncHeader();
}

/**
 * The theme's bag button, for the badge pulse a later phase adds.
 * Returned rather than animated here: P1 is page chrome, and nothing in this
 * phase adds anything to a cart.
 *
 * @returns {{ header: Element|null, bag: Element|null }}
 */
export function bagHooks() {
  const header = themeHeader();
  return {
    header,
    bag: header
      ? header.querySelector('[data-cart-icon], a[href$="/cart"], [href*="/cart"]')
      : null,
  };
}

/* -------------------------------------------------------------------------
   Wiring

   Everything is delegated from the document, so a section re-rendering in the
   theme editor rebinds nothing and still works.
   ------------------------------------------------------------------------- */

/* Where the current press started. A click event is dispatched on the nearest
   common ancestor of press and release, so selecting a review's text and
   releasing beyond the sheet edge targets the pop-out itself and would read as
   a scrim click. Only close when the press began on the scrim too. */
let pressOrigin = null;
document.addEventListener('mousedown', (event) => {
  pressOrigin = event.target;
});

document.addEventListener('click', (event) => {
  const el = popout();
  const origin = pressOrigin;
  pressOrigin = null;

  if (el && !el.hidden) {
    if (event.target === el && (origin === null || origin === el)) {
      closeReviews();
      return;
    }
    if (event.target.closest(SEL.popoutClose)) {
      closeReviews();
      return;
    }
  }

  const start = event.target.closest(SEL.start);
  if (start) {
    startBuilding();
    return;
  }

  const trigger = event.target.closest(SEL.open);
  if (trigger) openReviews(trigger);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeReviews();
    return;
  }
  trapFocus(event);
});

/* The theme editor replaces a section's DOM on every edit. If it replaces the
   reviews section while the pop-out is open, the node holding the open state is
   gone but <html> keeps the scroll lock — and the page can never be scrolled
   again. Release it whenever no pop-out is actually open. */
function releaseStrandedLock() {
  const el = popout();
  if (!el || el.hidden) {
    document.documentElement.classList.remove('byo-v4-scroll-locked');
    opener = null;
  }
}

document.addEventListener('shopify:section:unload', releaseStrandedLock);
document.addEventListener('shopify:section:load', releaseStrandedLock);

window.addEventListener('scroll', onScroll, { passive: true });
window.addEventListener('resize', onScroll, { passive: true });
document.addEventListener('shopify:section:load', onScroll);
syncHeader();

/* The snap (D70). Capture, so a control that stops its own event still
   counts as the first touch. */
document.addEventListener('pointerdown', engage, { capture: true, passive: true });
document.addEventListener('focusin', engage);
document.addEventListener('touchstart', () => {
  touching = true;
}, { passive: true });
['touchend', 'touchcancel'].forEach((type) =>
  document.addEventListener(type, () => {
    touching = false;
    settleSoon();
  }, { passive: true })
);
window.addEventListener('scroll', settleSoon, { passive: true });

export default { openReviews, closeReviews, startBuilding, reveal, bagHooks };
